import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatName } from '@/lib/utils';
import { ArrowLeft, Plus, Calendar, FileText, ClipboardList, CreditCard, Download, Edit, Trash2, Save, Heart, Baby, Users2, Link2, Pencil, Paperclip, FileDown, X, Loader2, AlertTriangle, Eye, FileX } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MedicalDiagnosisSelect, BvipDiagnosisSelect, DiagnosisPills } from '@/components/DiagnosisSelect';
import SearchableSelect from '@/components/SearchableSelect';
import CaseHistoryTab from '@/components/CaseHistoryTab';
import PhoneField from '@/components/PhoneField';

export default function PatientDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [linkedPartner, setLinkedPartner] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTxModal, setShowTxModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDiagnosisEdit, setShowDiagnosisEdit] = useState(false);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [noteForm, setNoteForm] = useState({ session_id: '', content: '', visibility: 'PRIVATE' });
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteForm, setEditNoteForm] = useState({ content: '', visibility: 'PRIVATE' });
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [assignForm, setAssignForm] = useState({ questionnaire_id: '', deadline_date: '' });
  const [txForm, setTxForm] = useState({ amount: '', payment_mode: 'CASH', payment_date: new Date().toISOString().split('T')[0], description: '', session_id: '' });
  const [editForm, setEditForm] = useState({});
  const [editMedDiag, setEditMedDiag] = useState([]);
  const [editBvipDiag, setEditBvipDiag] = useState([]);
  const [diagMedEdit, setDiagMedEdit] = useState([]);
  const [diagBvipEdit, setDiagBvipEdit] = useState([]);
  const [diagnosisTags, setDiagnosisTags] = useState('');
  const [caseHistoryStatus, setCaseHistoryStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  // Session edit/delete
  const [showEditSessionModal, setShowEditSessionModal] = useState(null);
  const [editSessionForm, setEditSessionForm] = useState({});
  const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(null);
  const [deleteSessionReason, setDeleteSessionReason] = useState('');
  // Transfer
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({ new_therapist_id: '', reason: '', recent_behaviour: '', additional_notes: '' });
  const [transferHistory, setTransferHistory] = useState([]);

  const isAdmin = user?.role === 'ADMIN';
  const isTherapist = user?.role === 'THERAPIST';
  const isPrimaryTherapist = isTherapist && client?.primary_therapist_id === user?._id;
  const canEditProfile = isAdmin || isPrimaryTherapist;
  const TERMINAL_STATUSES = ['COMPLETED', 'NO_SHOW', 'CANCELLED'];

  const fetchAll = async () => {
    try {
      const [cRes, sRes, nRes, aRes, tRes, qRes, thRes] = await Promise.all([
        api.get(`/clients/${id}`), api.get(`/sessions?client_id=${id}`),
        api.get(`/notes?client_id=${id}`), api.get(`/questionnaires/assignments/list?client_id=${id}`),
        api.get(`/transactions?client_id=${id}`), api.get('/questionnaires'), api.get('/therapists')
      ]);
      setClient(cRes.data); setSessions(sRes.data); setNotes(nRes.data);
      setAssignments(aRes.data); setTransactions(tRes.data?.items || tRes.data || []); setQuestionnaires(qRes.data);
      setTherapists(thRes.data);
      if (cRes.data.linked_partner_id) {
        try { const lpRes = await api.get(`/clients/${cRes.data.linked_partner_id}`); setLinkedPartner(lpRes.data); } catch {}
      }
      // Fetch case history status for tab alert
      try { const chStatus = await api.get(`/case-history/${id}/status`); setCaseHistoryStatus(chStatus.data); } catch {}
      // Fetch transfer history
      if (user?.role !== 'CLIENT') {
        try { const thRes = await api.get(`/clients/${id}/transfers`); setTransferHistory(thRes.data); } catch {}
      }
    } catch {}
  };

  useEffect(() => { fetchAll(); }, [id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/notes', { ...noteForm });
      const noteId = res.data._id;
      // Upload pending attachments
      for (const file of pendingAttachments) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/notes/${noteId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setShowNoteModal(false);
      setPendingAttachments([]);
      setNoteForm({ session_id: '', content: '', visibility: 'PRIVATE' });
      fetchAll();
    } catch {}
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setEditNoteForm({ content: note.content, visibility: note.visibility });
    setShowEditNoteModal(true);
  };

  const handleEditNote = async (e) => {
    e.preventDefault();
    if (!editingNote) return;
    try {
      await api.put(`/notes/${editingNote._id}`, editNoteForm);
      setShowEditNoteModal(false);
      setEditingNote(null);
      fetchAll();
    } catch {}
  };

  const handleAttachToNote = async (noteId, file) => {
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/notes/${noteId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchAll();
    } catch {} finally { setUploadingAttachment(false); }
  };

  const downloadAttachment = async (noteId, attachmentId, filename) => {
    try {
      const res = await api.get(`/notes/${noteId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const downloadReport = async (noteId) => {
    try {
      const res = await api.get(`/notes/${noteId}/report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `clinical_report_${noteId.slice(0,8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const deleteAttachment = async (noteId, attachmentId) => {
    try {
      await api.delete(`/notes/${noteId}/attachments/${attachmentId}`);
      fetchAll();
    } catch {}
  };

  const handleAssign = async (e) => { e.preventDefault(); await api.post('/questionnaires/assignments', { ...assignForm, client_id: id }); setShowAssignModal(false); fetchAll(); };

  const calcEnd = (st, dur) => { const [h, m] = st.split(':').map(Number); const em = h * 60 + m + dur; return `${String(Math.floor(em / 60)).padStart(2, '0')}:${String(em % 60).padStart(2, '0')}`; };

  const openEditSession = (s) => {
    setEditSessionForm({ title: s.title, description: s.description || '', session_type: s.session_type, date: s.date, start_time: s.start_time, end_time: s.end_time, meeting_link: s.meeting_link || '', branch_id: s.branch_id || '', duration: s.duration || 60 });
    setShowEditSessionModal(s);
  };
  const handleEditSession = async (e) => {
    e.preventDefault();
    if (!showEditSessionModal) return;
    try { await api.put(`/sessions/${showEditSessionModal._id}`, editSessionForm); setShowEditSessionModal(null); fetchAll(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteSession = async () => {
    if (!showDeleteSessionModal) return;
    try { await api.delete(`/sessions/${showDeleteSessionModal._id}`); setShowDeleteSessionModal(null); setDeleteSessionReason(''); fetchAll(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };
  const canEditSession = (s) => { if (TERMINAL_STATUSES.includes(s.status)) return false; if (isAdmin) return true; if (isTherapist && s.therapist_id === user._id && ['SCHEDULED', 'CHECKED_IN'].includes(s.status)) return true; return false; };
  const canDeleteSession = (s) => { if (TERMINAL_STATUSES.includes(s.status)) return false; if (isAdmin) return true; if (isTherapist && s.therapist_id === user._id && s.status === 'SCHEDULED') return true; return false; };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try { await api.post(`/clients/${id}/transfer`, transferForm); setShowTransferModal(false); setTransferForm({ new_therapist_id: '', reason: '', recent_behaviour: '', additional_notes: '' }); fetchAll(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };
  const handleAddTx = async (e) => { e.preventDefault(); await api.post('/transactions', { ...txForm, client_id: id, amount: parseFloat(txForm.amount) }); setShowTxModal(false); fetchAll(); };

  const downloadInvoice = async (txId) => {
    const res = await api.get(`/transactions/${txId}/invoice`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = `invoice_${txId}.pdf`; a.click();
  };

  const openEditModal = () => {
    setEditForm({
      first_name: client.first_name || '', last_name: client.last_name || '',
      phone: client.phone || '', date_of_birth: client.date_of_birth || '',
      gender: client.gender || '', blood_group: client.blood_group || '',
      address: client.address || '',
      emergency_contact_name: client.emergency_contact_name || '',
      emergency_contact_phone: client.emergency_contact_phone || '',
      previous_medical_issues: client.previous_medical_issues || '',
      primary_therapist_id: client.primary_therapist_id || '',
      caregiver: client.caregiver || null,
    });
    setEditMedDiag(client.medical_diagnosis || []);
    setEditBvipDiag(client.bvip_diagnosis || []);
    setShowEditModal(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    await api.put(`/clients/${id}`, { ...editForm, medical_diagnosis: editMedDiag, bvip_diagnosis: editBvipDiag });
    setShowEditModal(false);
    fetchAll();
  };

  const handleDiagnosisSave = async () => {
    await api.put(`/clients/${id}`, { medical_diagnosis: diagMedEdit, bvip_diagnosis: diagBvipEdit });
    setShowDiagnosisEdit(false);
    fetchAll();
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Are you sure you want to deactivate ${formatName(client)}?`)) return;
    await api.put(`/clients/${id}/deactivate`);
    fetchAll();
  };

  const handleActivate = async () => {
    await api.put(`/clients/${id}/activate`);
    fetchAll();
  };

  if (!client) return <div className="text-center py-10 text-[#4A6B68]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/patients')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">{formatName(client)}</h1>
            {client.client_type === 'CHILD' && <Badge className="bg-[#F5B041]/10 text-[#F5B041] text-[10px] border-0"><Baby className="w-3 h-3 mr-0.5" />Child</Badge>}
            {client.client_type === 'RELATIONSHIP' && <Badge className="bg-[#F5B041]/10 text-[#F5B041] text-[10px] border-0"><Heart className="w-3 h-3 mr-0.5" />Relationship</Badge>}
            {client.is_active === false && <Badge className="bg-[#D3544E]/10 text-[#D3544E] text-[10px] border-0">Inactive</Badge>}
          </div>
          <p className="text-sm text-[#4A6B68]">{client.email} · {client.phone}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Admin: Edit & Delete */}
          {isAdmin && (
            <>
              <Button size="sm" onClick={openEditModal} className="bg-white border border-[#E5F2F1] text-[#1A3330] hover:bg-[#F4FAF9] rounded-xl text-xs" data-testid="edit-client-btn"><Edit className="w-3 h-3 mr-1" />Edit</Button>
              {client.is_active !== false ? (
                <Button size="sm" onClick={handleDeactivate} className="bg-white border border-[#D3544E]/30 text-[#D3544E] hover:bg-[#D3544E]/10 rounded-xl text-xs" data-testid="delete-client-btn"><Trash2 className="w-3 h-3 mr-1" />Deactivate</Button>
              ) : (
                <Button size="sm" onClick={handleActivate} className="bg-white border border-[#2E8B57]/30 text-[#2E8B57] hover:bg-[#2E8B57]/10 rounded-xl text-xs" data-testid="activate-client-btn">Activate</Button>
              )}
            </>
          )}
          {/* Therapist: Only diagnosis edit */}
          {isTherapist && (
            <Button size="sm" onClick={() => { setDiagMedEdit(client.medical_diagnosis || []); setDiagBvipEdit(client.bvip_diagnosis || []); setShowDiagnosisEdit(true); }} className="bg-white border border-[#E5F2F1] text-[#1A3330] hover:bg-[#F4FAF9] rounded-xl text-xs" data-testid="edit-diagnosis-btn"><Edit className="w-3 h-3 mr-1" />Edit Diagnosis</Button>
          )}
          <Button size="sm" onClick={() => setShowNoteModal(true)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl text-xs" data-testid="add-note-btn"><FileText className="w-3 h-3 mr-1" />Add Note</Button>
          <Button size="sm" onClick={() => setShowAssignModal(true)} className="bg-[#0A6F64] hover:bg-[#0D8B7D] text-white rounded-xl text-xs" data-testid="assign-q-btn"><ClipboardList className="w-3 h-3 mr-1" />Assign</Button>
          <Button size="sm" onClick={() => setShowTxModal(true)} className="bg-[#F5B041] hover:bg-[#E59E29] text-white rounded-xl text-xs" data-testid="add-tx-btn"><CreditCard className="w-3 h-3 mr-1" />Transaction</Button>
        </div>
      </div>

      {/* Linked Partner Banner */}
      {linkedPartner && (
        <Card className="bg-[#F5B041]/5 border-[#F5B041]/20 rounded-2xl" data-testid="linked-partner-banner">
          <CardContent className="p-3 flex items-center gap-3">
            <Link2 className="w-4 h-4 text-[#F5B041]" />
            <p className="text-sm text-[#1A3330]">Linked partner: <button onClick={() => navigate(`/patients/${linkedPartner._id}`)} className="font-medium text-[#0D8B7D] hover:underline">{formatName(linkedPartner)}</button></p>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="patient-tabs">
        <TabsList className="bg-[#F4FAF9] rounded-xl p-1 w-fit flex-wrap">
          <TabsTrigger value="profile" data-testid="patient-profile-tab" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68]">Profile</TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68]">Sessions ({sessions.length})</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68]">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger
            value="case-history"
            className={`rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm ${
              caseHistoryStatus?.needs_alert
                ? 'text-[#D3544E] data-[state=active]:text-[#D3544E] font-semibold'
                : 'text-[#4A6B68] data-[state=active]:text-[#0D8B7D]'
            }`}
            data-testid="case-history-tab-trigger"
          >
            {caseHistoryStatus?.needs_alert && <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
            Case History
          </TabsTrigger>
          <TabsTrigger value="assessments" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68]">Assessments ({assignments.length})</TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68]" data-testid="patient-invoices-tab">Invoices</TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#0D8B7D] data-[state=active]:shadow-sm text-[#4A6B68]">Transactions ({transactions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4 space-y-4">
          {/* Primary & Secondary Therapist */}
          <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="therapist-info-card"><CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#1A3330] font-['Outfit']">Assigned Therapists</h3>
              {(isAdmin || isPrimaryTherapist) && (
                <Button size="sm" variant="outline" className="rounded-xl border-[#E5F2F1] text-[#0D8B7D] text-xs h-7" onClick={() => setShowTransferModal(true)} data-testid="transfer-client-btn">Transfer Client</Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><p className="text-xs text-[#4A6B68] uppercase tracking-wider">Primary Therapist</p><p className="text-sm font-medium text-[#1A3330] mt-1" data-testid="primary-therapist-name">{client.primary_therapist_name || 'Not Assigned'}</p></div>
              {client.secondary_therapist_names?.length > 0 && (
                <div><p className="text-xs text-[#4A6B68] uppercase tracking-wider">Secondary Therapists</p><p className="text-sm text-[#1A3330] mt-1" data-testid="secondary-therapist-names">{client.secondary_therapist_names.join(', ')}</p></div>
              )}
            </div>
          </CardContent></Card>

          {/* Transfer History */}
          {transferHistory.length > 0 && (isAdmin || isPrimaryTherapist) && (
            <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="transfer-history-card"><CardContent className="p-5">
              <h3 className="text-sm font-semibold text-[#1A3330] font-['Outfit'] mb-3">Transfer History</h3>
              <div className="space-y-2">{transferHistory.map((t, i) => (
                <div key={i} className="bg-[#F4FAF9] p-3 rounded-xl">
                  <div className="flex items-center justify-between"><p className="text-xs text-[#4A6B68]">{new Date(t.transfer_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p></div>
                  <p className="text-sm text-[#1A3330]"><span className="text-[#D3544E]">{t.from_therapist_name}</span> → <span className="text-[#2E8B57]">{t.to_therapist_name}</span></p>
                  <p className="text-xs text-[#4A6B68] mt-1">Reason: {t.reason}</p>
                </div>
              ))}</div>
            </CardContent></Card>
          )}

          <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="medical-info-section">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <h3 className="text-base font-semibold text-[#1A3330] font-['Outfit']">Medical Information</h3>
                {client.last_self_updated_at && (
                  <p className="text-[11px] text-[#4A6B68] italic" data-testid="last-self-updated">
                    Last updated by client on {new Date(client.last_self_updated_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[['Client Type', client.client_type || 'ADULT'], ['Date of Birth', client.date_of_birth], ['Gender', client.gender], ['Blood Group', client.blood_group],
                  ['Email', client.email], ['Phone', client.phone],
                  ['Address', client.address],
                  ['Emergency Contact', client.emergency_contact_name], ['Emergency Phone', client.emergency_contact_phone],
                  ['Previous Medical Issues', client.previous_medical_issues],
                  ['Menstrual Cycle Info', client.menstrual_cycle_info],
                  ['Pregnancy History', client.pregnancy_history]].map(([l, v]) => (
                  <div key={l}><p className="text-xs text-[#4A6B68] uppercase tracking-wider">{l}</p><p className="text-sm text-[#1A3330] mt-1 whitespace-pre-wrap">{v || 'N/A'}</p></div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Diagnosis Section */}
          {(client.medical_diagnosis?.length > 0 || client.bvip_diagnosis?.length > 0 || client.diagnosis_tags?.length > 0) && (
            <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="diagnosis-section">
              <CardContent className="p-6">
                <h3 className="text-base font-semibold text-[#1A3330] font-['Outfit'] mb-4">Diagnosis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#4A6B68] uppercase tracking-wider mb-2">Medical Diagnosis (ICD-11 / DSM-5-TR)</p>
                    {client.medical_diagnosis?.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {client.medical_diagnosis.map(d => (
                          <Badge key={d.code} className="bg-blue-100 text-blue-700 border-0 text-xs font-normal px-2 py-1">
                            <span className="font-semibold mr-1">{d.code}</span>{d.description}
                          </Badge>
                        ))}
                      </div>
                    ) : <p className="text-sm text-[#4A6B68]">None assigned</p>}
                  </div>
                  <div>
                    <p className="text-xs text-[#4A6B68] uppercase tracking-wider mb-2">BVIP Diagnosis (Brhdhi Vidya)</p>
                    {client.bvip_diagnosis?.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {client.bvip_diagnosis.map(b => (
                          <Badge key={b} className="bg-purple-100 text-purple-700 border-0 text-xs font-normal px-2 py-1">{b}</Badge>
                        ))}
                      </div>
                    ) : <p className="text-sm text-[#4A6B68]">None assigned</p>}
                  </div>
                </div>
                {client.diagnosis_tags?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E5F2F1]">
                    <p className="text-xs text-[#4A6B68] uppercase tracking-wider mb-1">Legacy Tags</p>
                    <div className="flex flex-wrap gap-1">{client.diagnosis_tags.map(t => <Badge key={t} className="bg-[#0D8B7D]/10 text-[#0D8B7D] text-xs border-0">{t}</Badge>)}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Caregiver Section */}
          {client.client_type === 'CHILD' && client.caregiver && (
            <Card className="bg-white border-[#F5B041]/20 rounded-2xl" data-testid="caregiver-info">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3"><Users2 className="w-4 h-4 text-[#F5B041]" /><h3 className="text-base font-semibold text-[#1A3330] font-['Outfit']">Caregiver Details</h3></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[['Name', client.caregiver.name], ['Relationship', client.caregiver.relationship], ['Phone', client.caregiver.phone], ['Email', client.caregiver.email], ['Address', client.caregiver.address]].map(([l, v]) => (
                    <div key={l}><p className="text-xs text-[#4A6B68] uppercase tracking-wider">{l}</p><p className="text-sm text-[#1A3330] mt-0.5">{v || 'N/A'}</p></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Medical Documents Section */}
          <MedicalDocumentsSection clientId={id} />
        </TabsContent>

        <TabsContent value="sessions" className="mt-4 space-y-3">
          {sessions.map(s => {
            const statusColors = { SCHEDULED: 'bg-[#3B82F6]/10 text-[#3B82F6]', CHECKED_IN: 'bg-[#0D8B7D]/10 text-[#0D8B7D]', COMPLETED: 'bg-[#2E8B57]/10 text-[#2E8B57]', NO_SHOW: 'bg-[#F5B041]/10 text-[#F5B041]', CANCELLED: 'bg-[#D3544E]/10 text-[#D3544E]' };
            const label = s.status === 'CHECKED_IN' ? 'Checked In' : s.status === 'NO_SHOW' ? 'No Show' : s.status;
            return (
            <Card key={s._id} className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#1A3330]">{s.title}</p>
                <p className="text-xs text-[#4A6B68]">{s.date} · {s.start_time}-{s.end_time} · {s.session_type}</p>
                {s.status === 'CANCELLED' && s.cancellation_reason && <p className="text-[10px] text-[#D3544E] italic mt-0.5">Reason: {s.cancellation_reason}</p>}
              </div>
              <div className="flex items-center gap-1.5">
                <Badge className={`text-[10px] border-0 ${statusColors[s.status] || ''}`}>{label}</Badge>
                {canEditSession(s) && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditSession(s)} data-testid={`edit-session-${s._id}`}><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>
                )}
                {canDeleteSession(s) && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setShowDeleteSessionModal(s); setDeleteSessionReason(''); }} data-testid={`delete-session-${s._id}`}><Trash2 className="w-3.5 h-3.5 text-[#D3544E]" /></Button>
                )}
              </div>
            </CardContent></Card>
          );})}
          {sessions.length === 0 && <p className="text-sm text-[#4A6B68]">No sessions yet.</p>}
        </TabsContent>

        <TabsContent value="notes" className="mt-4 space-y-3">
          {notes.map(n => {
            const canEdit = isAdmin || (isTherapist && n.therapist_id === user?._id);
            const canDownloadReport = isAdmin || isTherapist || n.visibility === 'PUBLIC';
            return (
            <Card key={n._id} className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={`note-card-${n._id}`}><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <p className="text-xs text-[#4A6B68]">{n.session_date} · {n.session_title} · {n.therapist_name}</p>
                  {n.last_edited_at && (
                    <span className="text-[10px] text-[#F5B041] italic" data-testid={`note-edited-label-${n._id}`}>
                      Edited {new Date(n.last_edited_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={`text-[10px] border-0 ${n.visibility === 'PUBLIC' ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-[#4A6B68]/10 text-[#4A6B68]'}`}>{n.visibility}</Badge>
                  {canEdit && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditNote(n)} data-testid={`edit-note-btn-${n._id}`}>
                      <Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" />
                    </Button>
                  )}
                  {canDownloadReport && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[#0D8B7D] text-[10px]" onClick={() => downloadReport(n._id)} data-testid={`download-report-btn-${n._id}`}>
                      <FileDown className="w-3.5 h-3.5 mr-1" />Report
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-[#1A3330] whitespace-pre-wrap">{n.content}</p>
              {/* Attachments */}
              {n.attachments && n.attachments.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E5F2F1]" data-testid={`note-attachments-${n._id}`}>
                  <p className="text-[11px] text-[#4A6B68] uppercase tracking-wider mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" />Attachments</p>
                  <div className="space-y-1.5">
                    {n.attachments.map(att => (
                      <div key={att.id} className="flex items-center justify-between bg-[#F4FAF9] rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5 text-[#0D8B7D]" />
                          <span className="text-xs text-[#1A3330]">{att.original_filename}</span>
                          <span className="text-[10px] text-[#4A6B68]">({(att.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => downloadAttachment(n._id, att.id, att.original_filename)} data-testid={`download-attachment-${att.id}`}>
                            <Download className="w-3.5 h-3.5 text-[#0D8B7D]" />
                          </Button>
                          {canEdit && (
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => deleteAttachment(n._id, att.id)} data-testid={`delete-attachment-${att.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-[#D3544E]" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Add attachment to existing note */}
              {canEdit && (
                <div className="mt-2">
                  <label className="cursor-pointer inline-flex items-center gap-1 text-[10px] text-[#0D8B7D] hover:text-[#0B786C] transition-colors">
                    <Paperclip className="w-3 h-3" />Attach file
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { if (e.target.files[0]) handleAttachToNote(n._id, e.target.files[0]); e.target.value = ''; }} />
                  </label>
                  {uploadingAttachment && <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-[#0D8B7D]" />}
                </div>
              )}
            </CardContent></Card>
          );})}
          {notes.length === 0 && <p className="text-sm text-[#4A6B68]">No notes yet.</p>}
        </TabsContent>


        <TabsContent value="case-history" className="mt-4 space-y-3">
          <CaseHistoryDocsBadge clientId={id} onClick={() => {
            setActiveTab('profile');
            setTimeout(() => {
              const el = document.querySelector('[data-testid="medical-documents-section"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
          }} />
          <CaseHistoryTab clientId={id} clientType={client?.client_type} linkedPartner={linkedPartner} clientName={client ? formatName(client) : ''} />
        </TabsContent>

        <TabsContent value="assessments" className="mt-4 space-y-3">
          {assignments.map(a => (
            <Card key={a._id} className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-4 flex items-center justify-between">
              <div><p className="text-sm font-medium text-[#1A3330]">{a.questionnaire_title}</p><p className="text-xs text-[#4A6B68]">Assigned: {a.assigned_date} · Deadline: {a.deadline_date}</p></div>
              <div className="flex items-center gap-2">
                {a.score !== null && a.score !== undefined && <span className="text-sm font-semibold text-[#0D8B7D]">Score: {a.score}</span>}
                <Badge className={`text-[10px] border-0 ${a.score_visibility === 'PRIVATE' ? 'bg-[#4A6B68]/10 text-[#4A6B68]' : 'bg-[#2E8B57]/10 text-[#2E8B57]'}`}>{a.score_visibility === 'PRIVATE' ? 'Private' : 'Public'}</Badge>
                <Badge className={`text-[10px] border-0 ${a.status === 'COMPLETED' ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : a.status === 'ASSIGNED' ? 'bg-[#0D8B7D]/10 text-[#0D8B7D]' : 'bg-[#D3544E]/10 text-[#D3544E]'}`}>{a.status}</Badge>
              </div>
            </CardContent></Card>
          ))}
          {assignments.length === 0 && <p className="text-sm text-[#4A6B68]">No assessments yet.</p>}
        </TabsContent>

        <TabsContent value="invoices" className="mt-4 space-y-3" data-testid="patient-invoices-content">
          <PatientInvoicesTab clientId={id} />
        </TabsContent>

        <TabsContent value="transactions" className="mt-4 space-y-3">
          {transactions.map(t => (
            <Card key={t._id} className="bg-white border-[#E5F2F1] rounded-2xl"><CardContent className="p-4 flex items-center justify-between">
              <div><p className="text-sm font-medium text-[#1A3330]">{t.description}</p><p className="text-xs text-[#4A6B68]">{t.invoice_number} · {t.payment_date} · {t.payment_mode}</p></div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#1A3330]">Rs.{t.amount?.toLocaleString()}</span>
                <Button size="sm" variant="ghost" onClick={() => downloadInvoice(t._id)} data-testid={`download-invoice-${t._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button>
              </div>
            </CardContent></Card>
          ))}
          {transactions.length === 0 && <p className="text-sm text-[#4A6B68]">No transactions yet.</p>}
        </TabsContent>
      </Tabs>

      {/* ===== ADMIN EDIT MODAL ===== */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg bg-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-client-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Edit Patient</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">First Name</Label><Input value={editForm.first_name} onChange={e => setEditForm({...editForm, first_name: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="edit-first-name" /></div>
              <div><Label className="text-xs">Last Name</Label><Input value={editForm.last_name} onChange={e => setEditForm({...editForm, last_name: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="edit-last-name" /></div>
              <div><Label className="text-xs">Phone</Label><PhoneField value={editForm.phone} onChange={v => setEditForm({...editForm, phone: v})} testId="edit-phone" /></div>
              <div><Label className="text-xs">DOB</Label><Input type="date" value={editForm.date_of_birth} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" /></div>
              <div><Label className="text-xs">Gender</Label>
                <Select value={editForm.gender} onValueChange={v => setEditForm({...editForm, gender: v})}><SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem><SelectItem value="Prefer not to say">Prefer not to say</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">Blood Group</Label>
                <Select value={editForm.blood_group} onValueChange={v => setEditForm({...editForm, blood_group: v})}><SelectTrigger className="rounded-xl border-[#E5F2F1] h-9 text-sm"><SelectValue placeholder="..." /></SelectTrigger><SelectContent>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" /></div>
              <div><Label className="text-xs">Emergency Contact</Label><Input value={editForm.emergency_contact_name} onChange={e => setEditForm({...editForm, emergency_contact_name: e.target.value})} className="rounded-xl border-[#E5F2F1] h-9 text-sm" /></div>
              <div><Label className="text-xs">Emergency Phone</Label><PhoneField value={editForm.emergency_contact_phone} onChange={v => setEditForm({...editForm, emergency_contact_phone: v})} testId="edit-emergency-phone" /></div>
              <div className="col-span-2"><Label className="text-xs">Primary Therapist <span className="text-[#D3544E]">*</span></Label>
                <SearchableSelect
                  value={editForm.primary_therapist_id}
                  onChange={(id) => setEditForm({ ...editForm, primary_therapist_id: id })}
                  options={therapists}
                  required
                  placeholder="Search therapist by name…"
                  testId="edit-primary-therapist"
                  getSearchText={(t) => `${t.first_name || ''} ${t.last_name || ''} ${t.email || ''}`}
                  getLabel={(t) => {
                    const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
                    return `Dr. ${fn} ${t.last_name || ''}`.trim();
                  }}
                  renderOption={(t) => {
                    const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
                    return (
                      <div>
                        <p className="text-sm font-medium text-[#1A3330] truncate">{`Dr. ${fn} ${t.last_name || ''}`.trim()}</p>
                        <p className="text-[11px] text-[#4A6B68] truncate">{t.email || '—'}</p>
                      </div>
                    );
                  }}
                  renderSelected={(t) => {
                    const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
                    return <span className="text-sm font-medium text-[#1A3330] truncate block">{`Dr. ${fn} ${t.last_name || ''}`.trim()}</span>;
                  }}
                />
              </div>
              <div className="col-span-2"><Label className="text-xs">Previous Medical Issues</Label><Textarea value={editForm.previous_medical_issues} onChange={e => setEditForm({...editForm, previous_medical_issues: e.target.value})} rows={2} className="rounded-xl border-[#E5F2F1] text-sm" /></div>
              <div className="col-span-2 border-t border-[#E5F2F1] pt-3">
                <p className="text-xs font-semibold text-[#1A3330] mb-2 uppercase tracking-wider">Diagnosis</p>
                <div className="space-y-3">
                  <MedicalDiagnosisSelect value={editMedDiag} onChange={setEditMedDiag} />
                  <BvipDiagnosisSelect value={editBvipDiag} onChange={setEditBvipDiag} />
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-edit-btn"><Save className="w-4 h-4 mr-2" />Save Changes</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== THERAPIST DIAGNOSIS EDIT MODAL ===== */}
      <Dialog open={showDiagnosisEdit} onOpenChange={setShowDiagnosisEdit}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="edit-diagnosis-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Edit Diagnosis</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#4A6B68]">As a therapist, you can modify diagnosis fields for your assigned clients.</p>
            <MedicalDiagnosisSelect value={diagMedEdit} onChange={setDiagMedEdit} />
            <BvipDiagnosisSelect value={diagBvipEdit} onChange={setDiagBvipEdit} />
            <Button onClick={handleDiagnosisSave} className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-diagnosis-btn">Save Diagnosis</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Modal */}
      <Dialog open={showNoteModal} onOpenChange={(open) => { setShowNoteModal(open); if (!open) setPendingAttachments([]); }}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="add-note-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Add Therapy Note</DialogTitle></DialogHeader>
          <form onSubmit={handleAddNote} className="space-y-4">
            <div><Label>Session</Label>
              <Select value={noteForm.session_id} onValueChange={v => setNoteForm({...noteForm, session_id: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue placeholder="Select session" /></SelectTrigger>
                <SelectContent>{sessions.map(s => <SelectItem key={s._id} value={s._id}>{s.title} - {s.date}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Textarea value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})} required rows={4} className="rounded-xl border-[#E5F2F1]" data-testid="note-content-input" /></div>
            <div><Label>Visibility</Label>
              <Select value={noteForm.visibility} onValueChange={v => setNoteForm({...noteForm, visibility: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="note-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="PRIVATE">Private (therapist only)</SelectItem><SelectItem value="PUBLIC">Public (shared with client)</SelectItem></SelectContent>
              </Select>
            </div>
            {/* Attachment upload */}
            <div>
              <Label className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" />Attach Documents</Label>
              <p className="text-[10px] text-[#4A6B68] mb-2">PDF, JPEG, PNG — max 10MB per file, max 5 files</p>
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F4FAF9] border border-[#E5F2F1] rounded-lg text-xs text-[#0D8B7D] hover:bg-[#E5F2F1] transition-colors" data-testid="attach-document-btn">
                <Paperclip className="w-3.5 h-3.5" />Choose Files
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" multiple onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setPendingAttachments(prev => [...prev, ...newFiles].slice(0, 5));
                  e.target.value = '';
                }} />
              </label>
              {pendingAttachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {pendingAttachments.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#F4FAF9] rounded-lg px-3 py-1.5">
                      <span className="text-xs text-[#1A3330] truncate max-w-[250px]">{f.name}</span>
                      <Button type="button" size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))} data-testid={`remove-pending-${i}`}>
                        <X className="w-3 h-3 text-[#D3544E]" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-note-btn">Save Note</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Note Modal */}
      <Dialog open={showEditNoteModal} onOpenChange={setShowEditNoteModal}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="edit-note-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Edit Therapy Note</DialogTitle></DialogHeader>
          <form onSubmit={handleEditNote} className="space-y-4">
            {editingNote && (
              <div className="bg-[#F4FAF9] p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">{editingNote.session_title || 'Session Note'}</p>
                <p className="text-xs text-[#4A6B68]">{editingNote.session_date} · {editingNote.therapist_name}</p>
              </div>
            )}
            <div><Label>Note</Label><Textarea value={editNoteForm.content} onChange={e => setEditNoteForm({...editNoteForm, content: e.target.value})} required rows={5} className="rounded-xl border-[#E5F2F1]" data-testid="edit-note-content-input" /></div>
            <div><Label>Visibility</Label>
              <Select value={editNoteForm.visibility} onValueChange={v => setEditNoteForm({...editNoteForm, visibility: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="edit-note-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="PRIVATE">Private (therapist only)</SelectItem><SelectItem value="PUBLIC">Public (shared with client)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-edit-note-btn"><Save className="w-4 h-4 mr-2" />Save Changes</Button>
              <Button type="button" variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowEditNoteModal(false)} data-testid="cancel-edit-note-btn">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Questionnaire Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="assign-q-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Assign Questionnaire</DialogTitle></DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div><Label>Questionnaire</Label>
              <Select value={assignForm.questionnaire_id} onValueChange={v => setAssignForm({...assignForm, questionnaire_id: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="assign-q-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{questionnaires.map(q => <SelectItem key={q._id} value={q._id}>{q.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Deadline</Label><Input type="date" value={assignForm.deadline_date} onChange={e => setAssignForm({...assignForm, deadline_date: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="assign-deadline-input" /></div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="assign-q-submit-btn">Assign</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Transaction Modal */}
      <Dialog open={showTxModal} onOpenChange={setShowTxModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="add-tx-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Add Transaction</DialogTitle></DialogHeader>
          <form onSubmit={handleAddTx} className="space-y-4">
            <div><Label>Amount (INR)</Label><Input type="number" value={txForm.amount} onChange={e => setTxForm({...txForm, amount: e.target.value})} required className="rounded-xl border-[#E5F2F1]" data-testid="tx-amount-input" /></div>
            <div><Label>Payment Mode</Label>
              <Select value={txForm.payment_mode} onValueChange={v => setTxForm({...txForm, payment_mode: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="tx-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CASH">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="CARD">Card</SelectItem><SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={txForm.payment_date} onChange={e => setTxForm({...txForm, payment_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="tx-date-input" /></div>
            <div><Label>Description</Label><Input value={txForm.description} onChange={e => setTxForm({...txForm, description: e.target.value})} required placeholder="Session fee, Package fee..." className="rounded-xl border-[#E5F2F1]" data-testid="tx-description-input" /></div>
            <Button type="submit" className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="tx-submit-btn">Add Transaction</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Session Modal */}
      <Dialog open={!!showEditSessionModal} onOpenChange={() => setShowEditSessionModal(null)}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid="patient-edit-session-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Edit Session</DialogTitle></DialogHeader>
          {showEditSessionModal && (
            <form onSubmit={handleEditSession} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title</Label><Input value={editSessionForm.title} onChange={e => setEditSessionForm({...editSessionForm, title: e.target.value})} required className="rounded-xl border-[#E5F2F1]" /></div>
                <div><Label>Type</Label>
                  <Select value={editSessionForm.session_type} onValueChange={v => setEditSessionForm({...editSessionForm, session_type: v})}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="IN_PERSON">In-Person</SelectItem><SelectItem value="VIDEO">Video</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Duration</Label>
                  <Select value={String(editSessionForm.duration)} onValueChange={v => { const d = parseInt(v); setEditSessionForm(f => ({...f, duration: d, end_time: calcEnd(f.start_time, d)})); }}>
                    <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue /></SelectTrigger>
                    <SelectContent>{[{v:30,l:'30 min'},{v:45,l:'45 min'},{v:60,l:'1 hour'},{v:90,l:'1.5 hours'},{v:120,l:'2 hours'}].map(d => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={editSessionForm.date} onChange={e => setEditSessionForm({...editSessionForm, date: e.target.value})} required className="rounded-xl border-[#E5F2F1]" /></div>
                <div><Label>Start Time</Label><Input type="time" value={editSessionForm.start_time} onChange={e => { const st = e.target.value; setEditSessionForm(f => ({...f, start_time: st, end_time: calcEnd(st, f.duration)})); }} required className="rounded-xl border-[#E5F2F1]" /></div>
                <div className="col-span-2"><Label>Meeting Link</Label><Input value={editSessionForm.meeting_link} onChange={e => setEditSessionForm({...editSessionForm, meeting_link: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
                <div className="col-span-2"><Label>Description</Label><Textarea value={editSessionForm.description} onChange={e => setEditSessionForm({...editSessionForm, description: e.target.value})} rows={2} className="rounded-xl border-[#E5F2F1]" /></div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="save-edit-session-btn">Save Changes</Button>
                <Button type="button" variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowEditSessionModal(null)}>Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Session Modal */}
      <Dialog open={!!showDeleteSessionModal} onOpenChange={() => setShowDeleteSessionModal(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="patient-delete-session-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Delete Session</DialogTitle></DialogHeader>
          {showDeleteSessionModal && (
            <div className="space-y-4">
              <p className="text-sm text-[#4A6B68]">Are you sure you want to permanently delete this session? This action cannot be undone.</p>
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium text-[#1A3330]">{showDeleteSessionModal.title}</p>
                <p className="text-xs text-[#4A6B68]">{showDeleteSessionModal.date} {showDeleteSessionModal.start_time}-{showDeleteSessionModal.end_time}</p>
              </div>
              <div><Label>Reason <span className="text-[#D3544E]">*</span></Label><Textarea value={deleteSessionReason} onChange={e => setDeleteSessionReason(e.target.value)} rows={2} className="rounded-xl border-[#E5F2F1] mt-1" placeholder="Reason..." data-testid="delete-session-reason" /></div>
              <div className="flex gap-2">
                <Button onClick={handleDeleteSession} disabled={!deleteSessionReason.trim()} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-delete-session-btn">Confirm Delete</Button>
                <Button variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowDeleteSessionModal(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Client Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="transfer-client-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#1A3330]">Transfer Client</DialogTitle></DialogHeader>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div className="bg-[#F4FAF9] p-3 rounded-xl">
              <p className="text-xs text-[#4A6B68]">Current Primary Therapist</p>
              <p className="text-sm font-medium text-[#1A3330]">{client?.primary_therapist_name || 'N/A'}</p>
            </div>
            <div><Label>Transfer To (New Primary Therapist) <span className="text-[#D3544E]">*</span></Label>
              <Select value={transferForm.new_therapist_id} onValueChange={v => setTransferForm({...transferForm, new_therapist_id: v})} required>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="transfer-therapist-select"><SelectValue placeholder="Select therapist" /></SelectTrigger>
                <SelectContent>{therapists.filter(t => t._id !== client?.primary_therapist_id).map(t => <SelectItem key={t._id} value={t._id}>{formatName(t)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Reason for Transfer <span className="text-[#D3544E]">*</span></Label><Textarea value={transferForm.reason} onChange={e => setTransferForm({...transferForm, reason: e.target.value})} required rows={2} className="rounded-xl border-[#E5F2F1]" placeholder="Why is this client being transferred?" data-testid="transfer-reason" /></div>
            <div><Label>Client's Recent Behaviour <span className="text-[#D3544E]">*</span></Label><Textarea value={transferForm.recent_behaviour} onChange={e => setTransferForm({...transferForm, recent_behaviour: e.target.value})} required rows={2} className="rounded-xl border-[#E5F2F1]" placeholder="Describe the client's recent behaviour..." data-testid="transfer-behaviour" /></div>
            <div><Label>Additional Notes</Label><Textarea value={transferForm.additional_notes} onChange={e => setTransferForm({...transferForm, additional_notes: e.target.value})} rows={2} className="rounded-xl border-[#E5F2F1]" placeholder="Any other handover information..." data-testid="transfer-notes" /></div>
            <div className="flex gap-2">
              <Button type="submit" disabled={!transferForm.new_therapist_id || !transferForm.reason.trim() || !transferForm.recent_behaviour.trim()} className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="confirm-transfer-btn">Confirm Transfer</Button>
              <Button type="button" variant="outline" className="rounded-xl border-[#E5F2F1]" onClick={() => setShowTransferModal(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PatientInvoicesTab({ clientId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [showPay, setShowPay] = useState(null);
  const [showCancel, setShowCancel] = useState(null);
  const [cancelForm, setCancelForm] = useState({ amount: '', reason: '' });
  const today = new Date().toISOString().split('T')[0];
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'CASH', payment_date: today, reference: '' });

  const STATUS = {
    DRAFT: 'bg-gray-100 text-gray-700',
    UNPAID: 'bg-[#F5B041]/10 text-[#F5B041]',
    PARTIALLY_PAID: 'bg-[#0A6F64]/10 text-[#0A6F64]',
    PAID: 'bg-[#2E8B57]/10 text-[#2E8B57]',
    CANCELLED: 'bg-[#D3544E]/10 text-[#D3544E]',
  };

  const fetchInvs = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/invoices?client_id=${clientId}&limit=200`);
      setItems(r.data.items || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchInvs(); /* eslint-disable-next-line */ }, [clientId]);

  const dlPdf = async (id) => {
    const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = `invoice_${id}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!showDelete) return;
    try {
      await api.delete(`/invoices/${showDelete._id}`);
      setShowDelete(null);
      fetchInvs();
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!showPay) return;
    try {
      await api.post(`/invoices/${showPay._id}/payments`, { ...payForm, amount: parseFloat(payForm.amount) });
      setShowPay(null);
      setPayForm({ amount: '', payment_mode: 'CASH', payment_date: today, reference: '' });
      fetchInvs();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleCancel = async (e) => {
    e.preventDefault();
    if (!showCancel) return;
    try {
      await api.post(`/invoices/${showCancel._id}/credit-note`, {
        amount: parseFloat(cancelForm.amount),
        reason: cancelForm.reason,
      });
      setShowCancel(null);
      setCancelForm({ amount: '', reason: '' });
      fetchInvs();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to cancel invoice'); }
  };

  return (
    <div className="space-y-3" data-testid="patient-invoices-tab">
      <div className="flex justify-end">
        <Button onClick={() => navigate(`/invoicing/invoice/new?client_id=${clientId}`)} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="patient-create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>
      <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4FAF9]">
              <TableHead className="text-xs">Invoice #</TableHead>
              <TableHead className="text-xs">Date</TableHead>
              <TableHead className="text-xs text-right">Total</TableHead>
              <TableHead className="text-xs text-right">Paid</TableHead>
              <TableHead className="text-xs text-right">Balance</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">Due</TableHead>
              <TableHead className="text-xs text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(inv => {
              const status = inv.is_overdue && (inv.status === 'UNPAID' || inv.status === 'PARTIALLY_PAID') ? 'OVERDUE' : inv.status;
              const isDraft = !!inv.is_draft;
              const balance = inv.balance_due || 0;
              return (
                <TableRow key={inv._id} data-testid={`patient-inv-row-${inv._id}`}>
                  <TableCell className="text-xs font-medium">{inv.invoice_number || 'DRAFT'}</TableCell>
                  <TableCell className="text-xs">{inv.invoice_date}</TableCell>
                  <TableCell className="text-xs text-right font-semibold">Rs.{(inv.grand_total||0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-right text-[#2E8B57]">Rs.{(inv.total_paid||0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-right text-[#D3544E]">Rs.{balance.toLocaleString()}</TableCell>
                  <TableCell><Badge className={`${STATUS[status] || ''} text-[10px] border-0`}>{status?.replace('_',' ')}</Badge></TableCell>
                  <TableCell className="text-xs">{inv.due_date || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/invoicing/invoice/${inv._id}`)} data-testid={`patient-view-inv-${inv._id}`}><Eye className="w-4 h-4 text-[#0D8B7D]" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => dlPdf(inv._id)} data-testid={`patient-dl-inv-${inv._id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button>
                      {isDraft && <Button variant="ghost" size="sm" onClick={() => navigate(`/invoicing/invoice/${inv._id}/edit`)} data-testid={`patient-edit-inv-${inv._id}`}><Pencil className="w-3.5 h-3.5 text-[#0D8B7D]" /></Button>}
                      {!isDraft && status !== 'CANCELLED' && balance > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => { setShowPay(inv); setPayForm({...payForm, amount: balance}); }} data-testid={`patient-pay-inv-${inv._id}`}><CreditCard className="w-4 h-4 text-[#0D8B7D]" /></Button>
                      )}
                      {isAdmin && status !== 'CANCELLED' && (
                        <Button variant="ghost" size="sm" title="Cancel Invoice" onClick={() => { setShowCancel(inv); setCancelForm({ amount: inv.grand_total || 0, reason: '' }); }} data-testid={`patient-cancel-inv-${inv._id}`}><FileX className="w-4 h-4 text-[#D3544E]" /></Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setShowDelete(inv)} data-testid={`patient-del-inv-${inv._id}`}><Trash2 className="w-3.5 h-3.5 text-[#D3544E]" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {!loading && items.length === 0 && <div className="p-6 text-center text-sm text-[#4A6B68]">No invoices yet.</div>}
      </Card>

      {/* Delete Modal */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="patient-del-inv-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Delete Invoice</DialogTitle></DialogHeader>
          {showDelete && (
            <div className="space-y-3">
              <p className="text-sm text-[#4A6B68]">Permanently delete this invoice? {!showDelete.is_draft && 'NOTE: Finalized invoices cannot be deleted; use credit note from invoice detail.'}</p>
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium">{showDelete.invoice_number || 'DRAFT'} · Rs.{showDelete.grand_total?.toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDelete} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-patient-del-inv">Confirm</Button>
                <Button variant="outline" onClick={() => setShowDelete(null)} className="rounded-xl border-[#E5F2F1]">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={!!showPay} onOpenChange={() => setShowPay(null)}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="patient-pay-modal">
          <DialogHeader><DialogTitle className="font-['Outfit']">Record Payment</DialogTitle></DialogHeader>
          {showPay && (
            <form onSubmit={handlePay} className="space-y-3">
              <div className="bg-[#F4FAF9] rounded-xl p-3 text-sm">
                <p>Invoice: <strong>{showPay.invoice_number}</strong></p>
                <p>Balance: <strong className="text-[#D3544E]">Rs.{(showPay.balance_due||0).toLocaleString()}</strong></p>
              </div>
              <div><Label>Amount *</Label><Input type="number" step="0.01" value={payForm.amount} required onChange={e => setPayForm({...payForm, amount: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="patient-pay-amount" /></div>
              <div><Label>Mode</Label>
                <Select value={payForm.payment_mode} onValueChange={v => setPayForm({...payForm, payment_mode: v})}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CASH">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="CARD">Card</SelectItem><SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={payForm.payment_date} onChange={e => setPayForm({...payForm, payment_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
              <div><Label>Reference</Label><Input value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="confirm-patient-pay">Record Payment</Button>
                <Button type="button" variant="outline" onClick={() => setShowPay(null)} className="rounded-xl border-[#E5F2F1]">Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Modal */}
      <Dialog open={!!showCancel} onOpenChange={() => setShowCancel(null)}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="patient-cancel-inv-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Cancel Invoice</DialogTitle></DialogHeader>
          {showCancel && (
            <form onSubmit={handleCancel} className="space-y-3">
              <div className="bg-[#D3544E]/5 p-3 rounded-xl">
                <p className="text-sm font-medium">{showCancel.invoice_number || 'DRAFT'} · Rs.{showCancel.grand_total?.toLocaleString()}</p>
                <p className="text-[11px] text-[#4A6B68] mt-1">{showCancel.is_draft ? 'Cancelling this draft will mark it as CANCELLED.' : 'A credit note will be generated to void the invoice.'} This action cannot be undone.</p>
              </div>
              <div><Label>Amount *</Label><Input type="number" step="0.01" value={cancelForm.amount} required onChange={e => setCancelForm({ ...cancelForm, amount: e.target.value })} className="rounded-xl border-[#E5F2F1]" data-testid="patient-cancel-inv-amount" /></div>
              <div><Label>Reason *</Label><Textarea rows={3} value={cancelForm.reason} required onChange={e => setCancelForm({ ...cancelForm, reason: e.target.value })} className="rounded-xl border-[#E5F2F1]" data-testid="patient-cancel-inv-reason" /></div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="patient-confirm-cancel-inv">Cancel Invoice</Button>
                <Button type="button" variant="outline" onClick={() => setShowCancel(null)} className="rounded-xl border-[#E5F2F1]">Close</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function MedicalDocumentsSection({ clientId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/uploads/medical-documents?client_id=${clientId}`);
      setDocs(r.data || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [clientId]);

  const openViewer = async (d) => {
    try {
      const res = await api.get(`/uploads/medical-documents/${d.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: d.content_type || 'application/octet-stream' }));
      setViewer({ url, type: d.content_type || '', name: d.filename || d.original_filename || 'document' });
    } catch {
      alert('Could not open document');
    }
  };

  const closeViewer = () => {
    if (viewer?.url) window.URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  const downloadDoc = async (d) => {
    const res = await api.get(`/uploads/medical-documents/${d.id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: d.content_type || 'application/octet-stream' }));
    const a = document.createElement('a'); a.href = url;
    a.download = d.filename || d.original_filename || 'document'; a.click();
    window.URL.revokeObjectURL(url);
  };

  const isPdf = (d) => (d.content_type || '').includes('pdf');
  const isImg = (d) => (d.content_type || '').startsWith('image/');
  const fileSize = (n) => n ? (n > 1024*1024 ? `${(n/1024/1024).toFixed(1)} MB` : `${Math.round((n||0)/1024)} KB`) : '';

  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid="medical-documents-section">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[#1A3330] font-['Outfit'] flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-[#0D8B7D]" /> Medical Documents
            <span className="text-xs font-normal text-[#4A6B68]">({docs.length} files)</span>
          </h3>
        </div>
        {loading && <p className="text-sm text-[#4A6B68]">Loading...</p>}
        {!loading && docs.length === 0 && <p className="text-sm text-[#4A6B68]">No documents uploaded yet.</p>}
        <div className="space-y-2">
          {docs.map(d => (
            <div key={d.id} className="flex items-center justify-between gap-3 p-3 border border-[#E5F2F1] rounded-xl hover:bg-[#F4FAF9]" data-testid={`doc-row-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isPdf(d) ? 'bg-[#D3544E]/10' : isImg(d) ? 'bg-[#0D8B7D]/10' : 'bg-[#F4FAF9]'}`}>
                  <FileText className={`w-5 h-5 ${isPdf(d) ? 'text-[#D3544E]' : 'text-[#0D8B7D]'}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A3330] truncate">{d.original_filename || d.filename}</p>
                  <p className="text-[11px] text-[#4A6B68]">
                    {fileSize(d.size)} · {new Date(d.created_at).toLocaleDateString()} · Uploaded by Client
                  </p>
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openViewer(d)} data-testid={`view-doc-${d.id}`}><Eye className="w-4 h-4 text-[#0D8B7D]" /></Button>
                <Button variant="ghost" size="sm" onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`}><Download className="w-4 h-4 text-[#0D8B7D]" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) closeViewer(); }}>
        <DialogContent className="max-w-4xl bg-white rounded-2xl p-0 overflow-hidden" data-testid="doc-viewer-modal">
          <DialogHeader className="px-6 py-4 border-b border-[#E5F2F1]">
            <DialogTitle className="font-['Outfit'] text-[#1A3330] truncate">{viewer?.name}</DialogTitle>
          </DialogHeader>
          {viewer && viewer.type.includes('pdf') && (
            <iframe src={viewer.url} title="PDF" className="w-full h-[75vh]" data-testid="pdf-iframe" />
          )}
          {viewer && viewer.type.startsWith('image/') && (
            <div className="overflow-auto max-h-[75vh] flex items-center justify-center bg-[#F4FAF9]">
              <img src={viewer.url} alt={viewer.name} className="max-w-full transition-transform hover:scale-150 cursor-zoom-in" data-testid="img-viewer" />
            </div>
          )}
          {viewer && !viewer.type.includes('pdf') && !viewer.type.startsWith('image/') && (
            <div className="p-6 text-center text-[#4A6B68]">Cannot preview this file type. Use Download.</div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function CaseHistoryDocsBadge({ clientId, onClick }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get(`/uploads/medical-documents?client_id=${clientId}`).then(r => setCount((r.data || []).length)).catch(() => {});
  }, [clientId]);
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0D8B7D]/10 hover:bg-[#0D8B7D]/20 rounded-full text-xs font-medium text-[#0D8B7D] transition-colors" data-testid="case-history-docs-badge">
      <Paperclip className="w-3.5 h-3.5" /> {count} Client Document{count === 1 ? '' : 's'}
    </button>
  );
}
